import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

const dashboardPaths = {
  superadmin: "/superadmin/properties",
  property: "/propertyownerdashboard/properties",
  nonprofit: "/501 C3 Corporations/MyPropertyServices",
  realestate: "/Hospital/HospitalSystems/properties",
  servicedashboard: "/servicedashboard/MyPropertyServices",
};

const getCurrentDashboard = (pathname) => {
  // Check for exact matches first
  for (const [key, path] of Object.entries(dashboardPaths)) {
    if (pathname.startsWith(path)) {
      return key;
    }
  }
  
  // Check for partial matches (like if the path is deeper)
  for (const key of Object.keys(dashboardPaths)) {
    if (pathname.includes(key)) {
      return key;
    }
  }
  
  // If still not found, try to determine from common patterns
  if (pathname.includes('propertyowner')) return 'property';
  if (pathname.includes('501 C3')) return 'nonprofit';
  if (pathname.includes('Hospital')) return 'realestate';
  if (pathname.includes('service')) return 'servicedashboard';
  
  return "superadmin"; // Default fallback
};

const ServiceInfoform = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBreadcrumbClick = () => {
    const currentDashboard = getCurrentDashboard(location.pathname);
    const path = dashboardPaths[currentDashboard];
    if (path) {
      navigate(path);
    } else {
      // Fallback or error handling
      navigate(dashboardPaths.superadmin);
    }
  };

  // Rest of your component...


  const { formData: initialData = {}, formId, formType } = location.state || {};

  const [formData, setFormData] = useState({
    companyName: "",
    address1: "",
    address2: "",
    phone: "",
    email: "",
    contactPerson: "",
    officeHours: "",
    website: "",
    industry1: "",
    industry2: "",
    serviceProvided1: "",
    specialityServices: "",
    advanceServiceShedule: "",
    serviceProvided2: "",
  });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  };

  const handleImageUpload = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setUploadFiles((prev) => [...prev, ...Array.from(files)]); // Store files directly
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("Authentication token is missing. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Step 1: Upload new files (if any)
      const newFiles = uploadFiles.filter((file) => file instanceof File);
      const filesURL = [];

      if (newFiles.length > 0) {
        const formDataForFiles = new FormData();
        newFiles.forEach((file) =>
          formDataForFiles.append("uploadFiles", file)
        );
        formDataForFiles.append("token", `"${token}"`);
        formDataForFiles.append(
          "foldername",
          "Service Provider | Service Form | Files"
        );

        const uploadResponse = await axios.post(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/upload-file",
          formDataForFiles,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (!uploadResponse.data.status || !uploadResponse.data.filesURL) {
          throw new Error(uploadResponse.data.message || "File upload failed.");
        }

        filesURL.push(...uploadResponse.data.filesURL);
      }

      // Step 2: Include pre-filled image URLs
      const preFilledFiles = uploadFiles.filter(
        (file) => typeof file === "string"
      );
      filesURL.push(...preFilledFiles);

      // Step 3: Prepare the payload
      const payload = {
        serviceFormData: {
          serviceDetails: {
            companyName: formData.companyName,
            address1: formData.address1,
            address2: formData.address2,
            phone: formData.phone,
            email: formData.email,
            contactPerson: formData.contactPerson,
            officeHours: formData.officeHours,
            website: formData.website,
            industry1: formData.industry1,
            serviceProvided1: formData.serviceProvided1,
            specialityServices: formData.specialityServices,
            advanceServiceShedule: formData.advanceServiceShedule,
            industry2: formData.industry2,
            serviceProvided2: formData.serviceProvided2,
          },
          uploadFiles: filesURL,
        },
        token: token,
      };

      let response;

      if (formId) {
        // Update existing service
        payload.form_id = formId;
        response = await axios.put(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/service-form/update",
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        // Create new service
        response = await axios.post(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/service-form/submit",
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      if (response.status === 200) {
        toast.success(
          formId
            ? "Service form updated successfully!"
            : "Service form submitted successfully!"
        );
        resetForm();
      } else {
        toast.error(
          response.data.error || "Failed to process the service form."
        );
      }
    } catch (error) {
      console.error("Error response:", error.response);
      toast.error(
        error.response?.data?.message ||
          "An unexpected error occurred. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form after successful submit or update
  const resetForm = () => {
    setFormData({
      companyName: "",
      address1: "",
      address2: "",
      phone: "",
      email: "",
      contactPerson: "",
      officeHours: "",
      website: "",
      industry1: "",
      serviceProvided1: "",
      specialityServices: "",
      advanceServiceShedule: "",
      industry2: "",
      serviceProvided2: "",
    });
    setUploadFiles([]);
  };

  const [isLoading, setIsLoading] = useState(true);

  // edit feature
  useEffect(() => {
    if (formType === "Service Form" && formId) {
      fetchFormData(formId);
    } else if (initialData?.serviceDetails) {
      setFormData(initialData.serviceDetails);
      setUploadFiles(initialData.uploadFiles || []);
    }
  }, [formId, formType, initialData]);

  const fetchFormData = async (formId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/service-form/get",
        { form_id: formId, token }
      );

      const formDataResponse = response.data?.form_data?.find(
        (item) => item._id === formId
      );

      if (formDataResponse) {
        setFormData(formDataResponse.serviceDetails);
        setUploadFiles(formDataResponse.uploadFiles || []);
      } else {
        toast.error("Form data not found.");
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      toast.error("Unable to load form data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFile = (index) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <ToastContainer />
      <div className="w-[75vw] mx-auto bg-white rounded-lg py-6">
        {/* Breadcrumbs */}
        <div className="mb-4 text-sm">
          <button
            onClick={handleBreadcrumbClick}
            className="text-gray-500 hover:text-gray-700"
          >
            post a service / property
          </button>
          <span className="mx-2 text-gray-400">&gt;</span>
          <span className="text-purple-600 font-medium">
            service information
          </span>
        </div>

        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <span role="img" aria-label="service-icon" className="mr-2">
              📄
            </span>{" "}
            Service Information
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block w-full text-gray-700 text-sm font-medium mb-1">
                Company Name
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Company Name"
                name="companyName"
                value={formData.companyName || ""}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Address Line 1
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Address Line 1"
                name="address1"
                value={formData.address1 || ""}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Address Line 2
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Address Line 2"
                name="address2"
                value={formData.address2}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Phone
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Email
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="email"
                placeholder="Email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Contact Person
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Contact Person"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Website
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
              />
            </div>

            {/* <div>
            <label className="block text-gray-700 text-sm font-medium mb-1">
              Geographical Area Serviced
            </label>
            <input
              className="border border-gray-300 rounded-md p-2 w-full"
              type="text"
              placeholder="Select Area"
              name="advanceServiceShedule"
              value={formData.advanceServiceShedule}
              onChange={handleInputChange}
            />
          </div> */}

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Industry
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Industry"
                name="industry1"
                value={formData.industry1}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Specialty Services
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Specialty Services"
                name="specialityServices"
                value={formData.specialityServices}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Services Schedule in Advance
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Services Schedule in Advance"
                name="advanceServiceShedule"
                value={formData.advanceServiceShedule}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Services Provided
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Services Provided"
                name="serviceProvided2"
                value={formData.serviceProvided2}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Office Hours
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Office Hours"
                name="officeHours"
                value={formData.officeHours}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Industry
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Industry"
                name="industry2"
                value={formData.industry2}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Services Provided
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full"
                type="text"
                placeholder="Services Provided"
                name="serviceProvided1"
                value={formData.serviceProvided1}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">
              Upload Files
            </h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                id="fileInput"
              />
              <label
                htmlFor="fileInput"
                className="cursor-pointer text-purple-600 font-semibold hover:underline"
              >
                Browse Files
              </label>
              <div className="mt-4">
                <p className="text-sm text-gray-600">
                  {uploadFiles.length > 0
                    ? `${uploadFiles.length} file(s) selected`
                    : "No files selected"}
                </p>
                {/* Display pre-filled images */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {uploadFiles.map((file, index) => (
                    <div key={index} className="relative">
                      {typeof file === "string" ? ( // Check if it's a URL (pre-filled image)
                        <img
                          src={file}
                          alt={`Uploaded file ${index}`}
                          className="w-20 h-20 object-cover rounded"
                        />
                      ) : (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Uploaded file ${index}`}
                          className="w-20 h-20 object-cover rounded"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="">
            <button
              type="submit"
              className={`bg-purple-700 w-full mt-5 h-10 rounded-lg text-white ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ServiceInfoform;
